import { Button, Input, Stack } from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { notify } from "@/ui/chakra/notify";
import { z } from "zod";
import { useUpdateClientEntity } from "../useCases/useUpdateClientEntities";
import { clientEntitySchema } from "../schemas/clientEntitySchema";
import { ClientEntity } from "../types/ClientEntity";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/ui/chakra/dialog";
import { Field } from "@/ui/chakra/field";

type ClientEntityFormInput = z.infer<typeof clientEntitySchema>;

export interface UpdateClientEntityModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedClientEntity: ClientEntity;
}

export const UpdateClientEntityModal = ({
  isOpen,
  onClose,
  selectedClientEntity,
}: UpdateClientEntityModalProps) => {
  const { isPending, mutate: updateClientEntity } = useUpdateClientEntity();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ClientEntityFormInput>({
    resolver: zodResolver(clientEntitySchema),
    defaultValues: {
      name: selectedClientEntity.name,
      description: selectedClientEntity.description,
      enabled: selectedClientEntity.enabled,
    },
  });

  const onFormSubmit = (data: ClientEntityFormInput) => {
    updateClientEntity(
      { id: selectedClientEntity.id, updatedClientEntity: data },
      {
        onSuccess: () => {
          notify.success("Empresa actualizada");
          onClose();
        },
        onError: (error) =>
          notify.error("No se pudo actualizar la empresa", error),
      },
    );
  };

  return (
    <DialogRoot isOpen={isOpen} onClose={onClose} isCentered size="md">
      <DialogContent>
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <DialogHeader>
            <DialogTitle>Editar empresa cliente</DialogTitle>
          </DialogHeader>
          <DialogCloseTrigger />
          <DialogBody>
            <Stack gap={4}>
              <Field
                label="Nombre"
                required
                invalid={!!errors.name}
                errorText={errors.name?.message}
              >
                <Input
                  {...register("name")}
                  placeholder="Nombre"
                  autoComplete="off"
                />
              </Field>
              <Field
                label="Descripción"
                invalid={!!errors.description}
                errorText={errors.description?.message}
              >
                <Input
                  {...register("description")}
                  placeholder="Descripción"
                  autoComplete="off"
                />
              </Field>
            </Stack>
          </DialogBody>

          <DialogFooter>
            <Button variant="outline" onClick={onClose} type="button">
              Cancelar
            </Button>
            <Button colorPalette="brand" type="submit" loading={isPending}>
              Guardar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </DialogRoot>
  );
};
